import React, { useEffect, useState, useCallback, useRef } from "react";

import { Calendar, dayjsLocalizer } from "react-big-calendar";
import dayjs from "dayjs";
import debounce from "lodash/debounce";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "antd/dist/reset.css";

import { Card, Flex, Image } from "antd";

import { ViewSessionModal } from "./modals";
import { NavigationControl } from "./NavigationControl";
import { MoreEventsDrawer } from "./drawers";
import { CustomWeekView } from "./CustomWeekView";
import { CustomDayView } from "./CustomDayView";

import { formatCompactAmount, toCapitalize } from "../../shared";

import { useCalendarState } from "../../hooks";
import { can } from "../../Auth";

const localizer = dayjsLocalizer(dayjs);

const statusColors = {
  pending: "#FFA500",
  completed: "#52c41a",
  cancel: "#f5222d",
};

const eventContainerStyle = {
  fontSize: 11,
  borderRadius: 6,
  padding: 4,
  color: "#fff",
  overflow: "hidden",
  minHeight: 40,
};

const topRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 4,
  fontWeight: 600,
};

const ellipsisStyle = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const tagStyle = {
  marginTop: 3,
  fontSize: 10,
  border: "1px solid rgba(255,255,255,0.4)",
  borderRadius: 4,
  padding: "1px 4px",
  width: "fit-content",
  maxWidth: 100,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const EventComponent = React.memo(
  ({ event }) => {
    const isShow = event?.alldata?.appointment?.is_edit;
    const medicalIssue = event?.alldata?.appointment?.client_info?.medical_info;
    return (
      <div
        style={{
          ...eventContainerStyle,
          backgroundColor: statusColors[event.status] || "#1677ff",
        }}
      >
        <div style={topRowStyle}>
          <div
            style={{
              ...ellipsisStyle,
              maxWidth: 80,
            }}
          >
            {event.patientNameFormatted}
          </div>

          {!isShow && can("Session Calendar Views", "view payment details") && (
            <span style={{ fontSize: 10 }}>
              {event.displayAmountFormatted} (PKR)
            </span>
          )}
        </div>

        <Flex align="flex-start" justify="space-between" gap={5}>
          <div style={tagStyle}>{event.serviceNameFormatted}</div>
          {medicalIssue &&
            Object.entries(medicalIssue).filter(
              ([key, value]) => key.startsWith("is") && value === "1",
            ).length > 0 && (
              <Image
                src={
                  event?.status === "cancel"
                    ? "/assets/images/issue-o.webp"
                    : "/assets/images/issue-r.webp"
                }
                width={5}
                height={"auto"}
                preview={false}
              />
            )}
        </Flex>
      </div>
    );
  },
  (prev, next) => prev.event.id === next.event.id,
);

const calendarComponents = {
  event: EventComponent,
};

const calendarViews = {
  month: true,
  week: CustomWeekView,
  day: CustomDayView,
};

const eventStyleGetter = () => ({
  style: {
    border: "none",
    padding: 0,
    background: "transparent",
  },
});

function SessionCalendar({ sessions = [], getSessions }) {
  const { date, view, setDate, setView, startDate, endDate } =
    useCalendarState();

  const [selectedSession, setSelectedSession] = useState(null);

  const [viewmorevisible, setViewMoreVisible] = useState(false);

  const [selectedsessiondate, setSelectedSessionDate] = useState(null);

  const [openViewSessionModal, setOpenViewSessionModal] = useState(false);

  const optimizedSessions = React.useMemo(() => {
    if (!Array.isArray(sessions)) return [];

    return sessions.map((event) => ({
      ...event,

      patientNameFormatted: toCapitalize(event.patientName),

      serviceNameFormatted: toCapitalize(event?.alldata?.service?.name),

      displayAmountFormatted: formatCompactAmount(event.displayAmount),
    }));
  }, [sessions]);

  const debounceRef = useRef(null);

  useEffect(() => {
    debounceRef.current = debounce((start, end) => {
      getSessions({
        startDate: start,
        endDate: end,
      });
    }, 250);

    return () => {
      debounceRef.current?.cancel();
    };
  }, [getSessions]);

  useEffect(() => {
    debounceRef.current?.(startDate, endDate);
  }, [startDate, endDate]);

  const handleEventClick = useCallback((event) => {
    setSelectedSession(event);
    setOpenViewSessionModal(true);
  }, []);

  const handleShowMore = useCallback((events, date) => {
    setViewMoreVisible(true);

    setSelectedSessionDate({
      data: events,
      sessionDate: date,
    });
  }, []);

  return (
    <>
      <Card size="small" className="border-gray no-padding">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 5,
          }}
        >
          <NavigationControl
            view={view}
            onViewChange={setView}
            currentDate={date}
            setDate={setDate}
          />

          <Calendar
            key={view}
            localizer={localizer}
            events={optimizedSessions}
            startAccessor="start"
            endAccessor="end"
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            views={calendarViews}
            components={calendarComponents}
            onSelectEvent={handleEventClick}
            toolbar={false}
            selectable={false}
            popup={true}
            step={30}
            timeslots={2}
            drilldownView={null}
            dayLayoutAlgorithm="no-overlap"
            allDayMaxRows={1}
            showAllEvents={false}
            popupOffset={20}
            eventPropGetter={eventStyleGetter}
            className={`my-calendar ${view === "month" ? "h-700" : ""}`}
            onDrillDown={() => {}}
            onShowMore={(events, date) => {
              handleShowMore(events, date);
              return false;
            }}
          />
        </div>
      </Card>

      {openViewSessionModal && (
        <ViewSessionModal
          open={openViewSessionModal}
          selectedSession={selectedSession}
          onClose={() => setOpenViewSessionModal(false)}
          call={getSessions}
        />
      )}

      {viewmorevisible && (
        <MoreEventsDrawer
          visible={viewmorevisible}
          selectedsessiondate={selectedsessiondate}
          handleEventClick={handleEventClick}
          onClose={() => setViewMoreVisible(false)}
        />
      )}
    </>
  );
}

export default React.memo(SessionCalendar);
